import { forwardRef, useId, useState, type InputHTMLAttributes } from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "../../lib/cn";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  error?: string;
  helperText?: string;
  /**
   * Suppresses the show/hide control on a password field.
   *
   * Reveal is on by default for `type="password"` rather than opt-in, so a new password field
   * cannot be added without it and quietly become the one inconsistent field in the app.
   */
  noReveal?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, noReveal, id, className, type, ...rest }, ref) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const helperId = `${inputId}-helper`;
    const errorId = `${inputId}-error`;

    const [revealed, setRevealed] = useState(false);
    const isPassword = type === "password";
    const showReveal = isPassword && !noReveal;
    // Only the rendered `type` changes. The value lives in the caller's state and is never
    // touched here, so what gets submitted is identical whether or not it is being displayed.
    const resolvedType = showReveal && revealed ? "text" : type;

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-text">
            {label}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type={resolvedType}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : helperText ? helperId : undefined}
            className={cn(
              "h-10 w-full rounded-control border border-border bg-surface px-3 text-sm text-text",
              "placeholder:text-text-muted",
              "transition-colors duration-fast ease-standard",
              // Border alone was the only focus signal and it is a 1px colour change — easy to miss.
              // The soft ring reuses the accent at 20% so it reads as focus, not as an error.
              "outline-none focus:border-accent focus:ring-2 focus:ring-accent/20",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              // Room for the toggle, plus Chrome/Edge's own password reveal control which would
              // otherwise sit on top of ours.
              showReveal && "pr-10 [&::-ms-reveal]:hidden [&::-webkit-credentials-auto-fill-button]:hidden",
              error && "border-danger focus:border-danger",
              className,
            )}
            {...rest}
          />

          {showReveal && (
            <button
              // type="button" is load-bearing: inside a <form>, a button with no type defaults
              // to submit, so toggling visibility would submit the login form.
              type="button"
              onClick={() => setRevealed((v) => !v)}
              aria-label={revealed ? "Hide password" : "Show password"}
              aria-pressed={revealed}
              // Not in the tab order of a password manager's way, but reachable by Tab, and
              // Enter/Space activate it natively because it is a real <button>.
              className={cn(
                "absolute right-1 top-1/2 inline-flex size-8 -translate-y-1/2 items-center justify-center",
                "rounded-control text-text-muted",
                "transition-colors duration-fast ease-standard",
                "hover:bg-surface-hover hover:text-text active:bg-surface-hover",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-surface",
              )}
            >
              {revealed ? (
                <EyeOff className="size-4" aria-hidden />
              ) : (
                <Eye className="size-4" aria-hidden />
              )}
            </button>
          )}
        </div>
        {error ? (
          <p id={errorId} role="alert" className="text-xs text-danger">
            {error}
          </p>
        ) : helperText ? (
          <p id={helperId} className="text-xs text-text-muted">
            {helperText}
          </p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = "Input";
